import { createAsyncThunk } from '@reduxjs/toolkit';

// Services
import { TransactionsService } from '../../services/transactions.service';

// Store entities
import { openNotification } from '../app-notifications/app-notifications.thunks';

// Slice
import { TransactionsSliceActions } from './transactions.slice';

// Models
import { ITransactionSettings } from '../../interfaces/transactions.interfaces';

export enum ETransactionsThunks {
  GetTransactions = 'TRANSACTIONS/getTransactions',
  RefundTransaction = 'TRANSACTIONS/refundTransaction',
  RefundCompanyTransaction = 'TRANSACTIONS/refundCompanyTransaction',
  GetReceiptUrl = 'TRANSACTIONS/getReceiptUrl',
  SaveTransactionsSetting = 'TRANSACTIONS/saveTransactionsSetting',
  ClearTransactions = 'TRANSACTIONS/clearTransactions',
  ChangeTransactionsRecording = 'TRANSACTIONS/changeTransactionsRecording',
  GetRecordedCall = 'TRANSACTIONS/getRecordedCall',
}

export const getTransactions = createAsyncThunk(
  ETransactionsThunks.GetTransactions,
  async ({ settings, isFirstLoad = false }: {
    settings: ITransactionSettings,
    isFirstLoad?: boolean
  }, { dispatch }) => {
    dispatch(TransactionsSliceActions.getTransactionsRequest({ isFirstLoad }));
    const transactions = await TransactionsService.fetchTransactions(settings);
    if (!transactions) {
      return dispatch(TransactionsSliceActions.getTransactionsRequestFailure('Error'));
    }
    return dispatch(TransactionsSliceActions.getTransactionsRequestSuccess(transactions));
  }
);

export const refundTransaction = createAsyncThunk(
  ETransactionsThunks.RefundTransaction,
  async ({ payment_id, refund_comment }: {
    payment_id: number,
    refund_comment: string
  }, { dispatch }) => {
    const { error, message } = await TransactionsService.refundTransaction(payment_id, refund_comment);
    if (error) {
      return dispatch(openNotification({
        type: 'error',
        description: error
      }));
    }
    dispatch(TransactionsSliceActions.updateTransactionInfo(payment_id));
    return dispatch(openNotification({
      type: 'success',
      description: message
    }));
  }
);

export const refundCompanyTransaction = createAsyncThunk(
  ETransactionsThunks.RefundCompanyTransaction,
  async ({ transaction_id, reason }: {
    transaction_id: number,
    reason: string
  }, { dispatch, getState }) => {
    const { error, message } = await TransactionsService.refundCompanyTransaction(transaction_id, reason);
    if (error) {
      dispatch(openNotification({
        type: 'error',
        description: error
      }));
      return { success: false };
    }
    dispatch(openNotification({
      type: 'success',
      description: message || 'Refund processed successfully'
    }));
    const state = getState() as any;
    const settings = state.transactions.settings;
    dispatch(getTransactions({ settings }));
    return { success: true };
  }
);

export const getReceiptUrl = createAsyncThunk(
  ETransactionsThunks.GetReceiptUrl,
  async ({ payment_id }: { payment_id: string | number }, { dispatch }) => {
    const { error, receipt_url } = await TransactionsService.getReceiptUrl(payment_id);
    if (!receipt_url) {
      dispatch(openNotification({
        type: 'error',
        description: error
      }));
      return null;
    }
    return receipt_url;
  }
);

export const saveTransactionsSetting = createAsyncThunk(
  ETransactionsThunks.SaveTransactionsSetting,
  async ({ settings, isFirstLoad = false }: { settings: ITransactionSettings, isFirstLoad: boolean }, { dispatch }) => {
    dispatch(TransactionsSliceActions.setSettings(settings));
    return dispatch(getTransactions({ settings, isFirstLoad }));
  }
);

export const getRecordedCall = createAsyncThunk(
  ETransactionsThunks.GetRecordedCall,
  async ({ transaction_id }: { transaction_id: number, }, { dispatch }) => {
    const res = await TransactionsService.getRecordedFile(transaction_id);
    return res;
  }
);

export const changeTransactionsRecording = createAsyncThunk(
  ETransactionsThunks.ChangeTransactionsRecording,
  async ({ transaction_id, has_recording_till }: { transaction_id: number, has_recording_till: string }, { dispatch }) => {
    return dispatch(TransactionsSliceActions.changeTransactionsRecordingData({ transaction_id, has_recording_till }));
  }
);

export const clearTransactions = createAsyncThunk(
  ETransactionsThunks.ClearTransactions,
  async (_, { dispatch }) => {
    return dispatch(TransactionsSliceActions.clearTransactions());
  }
);
